import type { Key } from 'react';
import { ICustomerUtilityBill } from './interface';
import { IPagination } from '../IPagination';
import { API } from '../api';

// Data layer for Customer Utility Bills.
//
// Customer utility bills are served by the real backend (server-side paginated/filtered/sorted).
// Import jobs use the generic async-job endpoint (POST/GET /async-job, jobName=import-customer-utility-bills)
// via src/domain/async-job. The per-bill audit trail is NOT here — it uses the generic audit-trail
// endpoint via useAuditTrailListQuery (entityType=CustomerUtilityBillEntity).

export interface ICustomerUtilityBillsQuery {
    search?: string;
    ids?: string; // CSV of ids — filters to this explicit set, e.g. to poll live status during a batch action
    statuses?: string; // CSV of BillProcessingStatus
    verified?: 'true' | 'false';
    utilities?: string; // CSV
    periodStart?: string; // ISO
    periodEnd?: string; // ISO
    sortField?: string;
    sortOrder?: 'ascend' | 'descend';
    offset?: number;
    limit?: number;
}

// Reads a JSON response, returning its body on success and throwing the backend error message (with a
// caller-supplied fallback) otherwise. Shared by every JSON endpoint below so the ok/throw dance lives once.
const readJsonResponse = async <T>(response: Response, fallbackError: string): Promise<T> => {
    const respBody: any = await response.json();
    if (response.ok) {
        return respBody as T;
    }
    throw new Error(respBody?.error?.message || fallbackError);
};

// Filter + sort params shared by the list and XLSX-export endpoints.
const billFilterParams = (query: ICustomerUtilityBillsQuery): Record<string, string> => ({
    ...(query.statuses && { statuses: query.statuses }),
    ...(query.verified && { verified: query.verified }),
    ...(query.utilities && { utilities: query.utilities }),
    ...(query.periodStart && { periodStart: query.periodStart }),
    ...(query.periodEnd && { periodEnd: query.periodEnd }),
    ...(query.sortField && { sortField: query.sortField }),
    ...(query.sortOrder && { sortOrder: query.sortOrder }),
});

const JSON_HEADERS = { 'Content-Type': 'application/json' };

// Editable fields when an operator reviews/corrects a parsed bill. Only provided fields are changed; an
// explicit `null` clears a field (lets the operator blank out a wrong value). verifiedAt / verifiedBy are
// stamped server-side from the request actor; the client only sets `verify`.
export interface IUpdateCustomerUtilityBill {
    siteName?: string | null;
    siteNumber?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    accountNumber?: string | null;
    enrollmentId?: string | null;
    meterNumber?: string | null;
    rateClass?: string | null;
    controlProviderName?: string | null;
    utility?: string | null;
    market?: string | null;
    zone?: string | null;
    zipCode?: string | null;
    billingKw?: number | null;
    billingKwh?: number | null;
    billingPeriodStart?: string | null; // ISO
    billingPeriodEnd?: string | null; // ISO
    notes?: string | null;
    verify?: boolean;
}

// Per-id outcome of a batch verify: ids that weren't verifiable are skipped and reported back.
export interface IBatchVerifyResult {
    verifiedIds: string[];
    skipped: { id: string; reason: string }[];
}

// Per-id outcome of a batch re-parse: `reparsedIds` are the bills queued for re-parsing; ids that are missing,
// not in a reparseable status, or whose enqueue failed are skipped and reported back in `skipped`.
export interface IBatchReparseResult {
    reparsedIds: string[];
    skipped: { id: string; reason: string }[];
}

export const fetchCustomerUtilityBills = async (
    query: ICustomerUtilityBillsQuery,
    signal?: AbortSignal | null
): Promise<IPagination<ICustomerUtilityBill>> => {
    const path = `/customer-utility-bills?${new URLSearchParams({
        ...(query.search && { search: query.search }),
        ...(query.ids && { ids: query.ids }),
        ...billFilterParams(query),
        ...(query.offset != null && { offset: String(query.offset) }),
        ...(query.limit != null && { limit: String(query.limit) }),
    })}`;

    const response = await API.fetch(path, {
        method: 'get',
        headers: JSON_HEADERS,
        ...(signal && { signal }),
    });

    return readJsonResponse<IPagination<ICustomerUtilityBill>>(response, `Couldn’t load bills`);
};

// Download the bills list as an XLSX file. The caller decides what to send: the current filters, search and sort
// ("Apply Filters"), or only the sort ("Download all"). Pagination is never sent, so the export covers every matching
// record. `fields` narrows the file to the given column keys in order (omit for all columns). The response is an
// attachment, so we read it as a blob and save it via a transient object-URL anchor.
export const exportCustomerUtilityBillsToXlsx = async (
    query: ICustomerUtilityBillsQuery,
    fields?: string[],
    signal?: AbortSignal | null
): Promise<void> => {
    const path = `/customer-utility-bills?${new URLSearchParams({
        format: 'xlsx',
        ...(query.search && { search: query.search }),
        ...billFilterParams(query),
        ...(fields?.length && { fields: fields.join(',') }),
    })}`;

    const response = await API.fetch(path, {
        method: 'get',
        ...(signal && { signal }),
    });

    if (!response.ok) {
        // Errors come back as JSON even though the success path is a binary file.
        const respBody = await response.json().catch(() => null);
        throw new Error(respBody?.error?.message || `Couldn’t export the bills`);
    }

    const blob = await response.blob();
    const fileName = filenameFromContentDisposition(response.headers.get('Content-Disposition')) ?? 'customer-utility-bills.xlsx';

    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
};

// Table column key -> XLSX export column key, where they differ ('billingPeriod' expands to start & end columns).
const EXPORT_FIELD_BY_COLUMN_KEY: Record<string, string> = { period: 'billingPeriod' };

// Table columns with no data to export.
const NON_EXPORTABLE_COLUMN_KEYS = ['actions'];

// Maps the table's visible columns (already in display order) to the `fields` list for the "Current view" export.
export const toBillExportFields = (columns: { key?: Key }[]): string[] =>
    columns
        .map(column => column.key)
        .filter((key): key is string => typeof key === 'string' && !NON_EXPORTABLE_COLUMN_KEYS.includes(key))
        .map(key => EXPORT_FIELD_BY_COLUMN_KEY[key] ?? key);

// Pulls the server-suggested filename out of a Content-Disposition header (e.g. `attachment; filename=foo.xlsx`).
const filenameFromContentDisposition = (header: string | null): string | null => {
    if (!header) return null;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match ? decodeURIComponent(match[1]) : null;
};

// Fetch a single bill by id. Used to poll live re-parse status from the edit modal, since a re-parse runs
// asynchronously on the backend after the reparse request is accepted.
export const fetchCustomerUtilityBillById = async (id: string, signal?: AbortSignal | null): Promise<ICustomerUtilityBill> => {
    const path = `/customer-utility-bills/${id}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: JSON_HEADERS,
    });

    return readJsonResponse<ICustomerUtilityBill>(response, `Couldn’t load the bill`);
};

// Update (review/correct) a single bill. Rejected by the backend while the record is still being parsed.
export const updateCustomerUtilityBill = async (
    id: string,
    body: IUpdateCustomerUtilityBill,
    signal?: AbortSignal | null
): Promise<ICustomerUtilityBill> => {
    const path = `/customer-utility-bills/${id}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'PUT',
        headers: JSON_HEADERS,
        body: JSON.stringify(body),
    });

    return readJsonResponse<ICustomerUtilityBill>(response, `Couldn’t update the bill`);
};

// Verify (verify=true) or un-verify (verify=false) a batch of parsed bills as the current user. Ids that
// are missing or not in a verifiable status are skipped and reported back in `skipped`; `verifiedIds` holds
// the ids whose verification state was actually changed.
const setCustomerUtilityBillsVerification = async (ids: string[], verify: boolean, signal?: AbortSignal | null): Promise<IBatchVerifyResult> => {
    const path = `/customer-utility-bills/verify`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ ids, verify }),
    });

    return readJsonResponse<IBatchVerifyResult>(response, `Couldn’t ${verify ? 'verify' : 'unverify'} the selected bills`);
};

// Used for both the single-row and bulk verify actions.
export const verifyCustomerUtilityBills = (ids: string[], signal?: AbortSignal | null): Promise<IBatchVerifyResult> =>
    setCustomerUtilityBillsVerification(ids, true, signal);

export const unverifyCustomerUtilityBills = (ids: string[], signal?: AbortSignal | null): Promise<IBatchVerifyResult> =>
    setCustomerUtilityBillsVerification(ids, false, signal);

// Distinct, non-empty utility names across all bills — used to populate the utility sidebar filter.
export const fetchCustomerUtilityBillUtilities = async (signal?: AbortSignal | null): Promise<string[]> => {
    const path = `/customer-utility-bills/utilities`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: JSON_HEADERS,
    });

    return readJsonResponse<string[]>(response, `Couldn’t load utility filter options`);
};

// Request a re-parse of a batch of bills: the backend marks each eligible bill REPARSE_QUEUED and publishes a
// parse message per bill. Missing / non-reparseable / failed-to-enqueue ids come back in `skipped`.
export const reparseCustomerUtilityBills = async (ids: string[], signal?: AbortSignal | null): Promise<IBatchReparseResult> => {
    const path = `/customer-utility-bills/reparse`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ ids }),
    });

    return readJsonResponse<IBatchReparseResult>(response, `Couldn’t re-parse the selected bills`);
};
